import React, { useMemo, useState } from "react";
import { Table, Tag, Tooltip, Space, Pagination } from "antd";
import { Link } from "react-router-dom";
import {
  ColumnOrderManager,
  useDraggableColumnOrder,
} from "../column-order-manager";
import {
  marcacaoDoFrete,
  MARCACAO,
  CORES_DA_MARCACAO,
  ROTULOS_DA_MARCACAO,
} from "../../util/freight/acompanhamento-marcacoes";
import {
  CORES_DO_ALERTA,
  detalheDoAlerta,
  ordenarAlertas,
  rotuloDoAlerta,
} from "../../util/freight/alertas-do-frete";

const TRACO = "—";

function formatarData(valor) {
  if (!valor) return TRACO;
  const partes = String(valor).split("-");
  if (partes.length !== 3) return String(valor);
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

/** Aceita millis, ISO e Timestamp serializado — os três formatos que a base tem. */
function paraMillis(valor) {
  if (valor == null) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor === "string") {
    const d = new Date(valor).getTime();
    return Number.isNaN(d) ? null : d;
  }
  if (typeof valor._seconds === "number") {
    return valor._seconds * 1000 + Math.floor((valor._nanoseconds || 0) / 1e6);
  }
  if (typeof valor.seconds === "number") {
    return valor.seconds * 1000 + Math.floor((valor.nanoseconds || 0) / 1e6);
  }
  return null;
}

function haQuantoTempo(millis) {
  if (!millis) return TRACO;
  const minutos = Math.max(0, Math.floor((Date.now() - millis) / 60000));
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas}h${String(minutos % 60).padStart(2, "0")}`;
  return `há ${Math.floor(horas / 24)} dias`;
}

function TabelaAcompanhamento({
  fretes,
  carregando,
  paginacao,
  freteSelecionado,
  onSelecionar,
  onMudarPagina,
  onMudarTamanhoDaPagina,
}) {
  const [ordemDasColunas, setOrdemDasColunas] = useState(null);

  const colunas = useMemo(
    () => [
      {
        title: "Código do frete",
        key: "numberSerial",
        width: 130,
        render: (_, frete) => (
          <Link to={`/freightsDetail/${frete.id}/${frete?.driver?.uidDriver || ""}`}>
            {frete.numberSerial ?? TRACO}
          </Link>
        ),
      },
      {
        title: "Motorista",
        key: "motorista",
        width: 150,
        render: (_, frete) => frete?.driver?.name || "Não informado",
      },
      {
        title: "Rota",
        key: "rota",
        width: 240,
        render: (_, frete) => {
          const origem = frete?.firstDelivery;
          const destino = frete?.lastDelivery;
          if (!origem?.city && !destino?.city) return TRACO;
          return `${origem?.city || "?"} - ${origem?.uf || "?"} → ${destino?.city || "?"} - ${destino?.uf || "?"}`;
        },
      },
      {
        title: "Status",
        key: "status",
        width: 140,
        render: (_, frete) => {
          const marcacao = marcacaoDoFrete(frete);
          // Fora das cinco marcações da tela, mostra o status como ele está na base — "Contratado",
          // "Cancelado". Rotular tudo de "Outro status" esconderia do operador o que o frete é.
          const rotulo =
            marcacao === MARCACAO.OUTRO
              ? frete?.status?.describe || ROTULOS_DA_MARCACAO.OUTRO
              : ROTULOS_DA_MARCACAO[marcacao];
          return <Tag color={CORES_DA_MARCACAO[marcacao]}>{rotulo}</Tag>;
        },
      },
      {
        title: "Previsão entrega",
        key: "previsao",
        width: 140,
        render: (_, frete) => formatarData(frete?.lastDelivery?.dateColeta),
      },
      {
        title: "Atraso",
        key: "atraso",
        width: 90,
        render: (_, frete) => {
          const dias = frete._diasDeAtraso;
          if (!dias) return TRACO;
          return (
            <span className="acomp-tabela__atraso">
              +{dias} {dias === 1 ? "dia" : "dias"}
            </span>
          );
        },
      },
      {
        title: "Placa",
        key: "placa",
        width: 110,
        render: (_, frete) => frete.plate || TRACO,
      },
      {
        title: "Última posição",
        key: "ultimaPosicao",
        width: 150,
        render: (_, frete) => {
          const millis = paraMillis(frete?.lastPosition?.timestamp);
          if (!millis) return TRACO;
          // `legacy` marca a posição lida da subcoleção antiga, sem os campos da captura nova.
          const origem =
            frete.lastPositionSource === "legacy"
              ? "Formato anterior, sem precisão nem origem da captura"
              : "Captura do app novo";
          return (
            <Tooltip title={origem}>
              <span>{haQuantoTempo(millis)}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Tempo no status",
        key: "tempoNoStatus",
        width: 140,
        render: (_, frete) => {
          const millis = paraMillis(frete?.lastStatusChange?.occurredAt);
          return millis ? haQuantoTempo(millis) : TRACO;
        },
      },
      {
        title: "Ocorrências",
        key: "alertas",
        width: 200,
        render: (_, frete) => {
          const alertas = ordenarAlertas(frete?.alertas);
          if (!alertas.length) return TRACO;
          return (
            <Space size={[4, 4]} wrap>
              {alertas.map((alerta) => (
                <Tooltip key={alerta.chave} title={detalheDoAlerta(alerta)}>
                  <Tag color={CORES_DO_ALERTA[alerta.chave]}>
                    {rotuloDoAlerta(alerta)}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          );
        },
      },
    ],
    [],
  );

  // Sem `lockedKeys`: no componente deles, coluna travada vai para o FIM da tabela — foi feito
  // para a coluna de ações. Aqui todas as colunas são móveis, e a ordem base já começa pelo código.
  const { orderedColumns, tableComponents } = useDraggableColumnOrder({
    baseColumns: colunas,
    columnOrder: ordemDasColunas,
    onColumnOrderChange: setOrdemDasColunas,
  });

  return (
    <div className="acomp-tabela">
      <div className="acomp-tabela__barra">
        <span className="acomp-tabela__titulo">
          {paginacao.total == null ? "Entregas" : `Entregas (${paginacao.total})`}
        </span>
        <Space size={8}>
          <ColumnOrderManager
            columns={colunas}
            value={ordemDasColunas}
            onChange={setOrdemDasColunas}
          />
        </Space>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={carregando}
        columns={orderedColumns}
        components={tableComponents}
        dataSource={fretes}
        pagination={false}
        scroll={{ x: 900 }}
        onRow={(frete) => ({
          onClick: () => onSelecionar(frete),
          className:
            freteSelecionado?.id === frete.id
              ? "acomp-tabela__linha--ativa"
              : undefined,
        })}
        locale={{ emptyText: "Nenhum frete no filtro atual." }}
      />

      <div className="acomp-tabela__rodape">
        <span>
          {!fretes.length
            ? "Nenhum registro"
            : paginacao.total == null
              ? `Mostrando ${paginacao.primeiro} a ${paginacao.ultimo}`
              : `Mostrando ${paginacao.primeiro} a ${paginacao.ultimo} de ${paginacao.total} registros`}
        </span>
        <Space size={8}>
          {/* Com o total conhecido dá para numerar as páginas. Na busca por placa ou por número não
              há total, e aí sobra o par anterior/próxima — que é o que o cursor sabe fazer. */}
          {paginacao.total == null ? (
            <>
              <button
                type="button"
                className="acomp-tabela__botao"
                disabled={paginacao.pagina <= 1 || carregando}
                onClick={() => onMudarPagina(paginacao.pagina - 1)}
              >
                Anterior
              </button>
              <span className="acomp-tabela__pagina">{paginacao.pagina}</span>
              <button
                type="button"
                className="acomp-tabela__botao"
                disabled={!paginacao.hasMore || carregando}
                onClick={() => onMudarPagina(paginacao.pagina + 1)}
              >
                Próxima
              </button>
            </>
          ) : (
            <Pagination
              size="small"
              current={paginacao.pagina}
              total={paginacao.total}
              pageSize={paginacao.tamanho}
              showSizeChanger={false}
              disabled={carregando}
              onChange={onMudarPagina}
            />
          )}
          <select
            className="acomp-tabela__tamanho"
            value={paginacao.tamanho}
            onChange={(e) => onMudarTamanhoDaPagina(Number(e.target.value))}
          >
            {[8, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n} por página
              </option>
            ))}
          </select>
        </Space>
      </div>
    </div>
  );
}

export default React.memo(TabelaAcompanhamento);
